import { defineStore } from 'pinia';
import { Geolocation } from '@capacitor/geolocation';
import { ref } from 'vue';
import { useLocationPermission } from './permissions/location-permission.store';
import { isPlatform } from '@ionic/vue';
import { usePermissionAlert } from '@/composables/use-permission-alert';

export const useLocation = defineStore('location', () => {
  const locationPermission = useLocationPermission();
  /**
   * [long,lat]
   */
  const coordinates = ref<[number, number]>();
  const lastFetchedAt = ref<Date>();
  const { showAlert } = usePermissionAlert('Location');
  const loadingCoordinates = ref(false);
  async function updateCoordinatesWeb() {
    try {
      const res = await new Promise<{ position?: GeolocationPosition; error?: any }>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (position) => resolve({ position }),
          (err) => resolve({ error: err })
        );
      });
      if (res.position) {
        coordinates.value = [res.position.coords.longitude, res.position.coords.latitude];
        lastFetchedAt.value = new Date();
      } else {
        coordinates.value = undefined;
        lastFetchedAt.value = undefined;
      }
    } catch (e) {
      console.log(`Error getting current position: ${e}`);
    }
  }

  async function updateCoordinatesNative() {
    try {
      const response = await Geolocation.getCurrentPosition({ maximumAge: 60_000 });
      if (response.coords) {
        coordinates.value = [response.coords.longitude, response.coords.latitude];
        lastFetchedAt.value = new Date();
      } else {
        coordinates.value = undefined;
        lastFetchedAt.value = undefined;
      }
    } catch (e) {
      console.log(`Error getting current position: ${e}`);
    }
  }

  async function updateCoordinates(showAlertOnFailure = false) {
    loadingCoordinates.value = true;
    await locationPermission.prompt();
    if (locationPermission.ready) {
      if (isPlatform('hybrid')) {
        await updateCoordinatesNative();
      } else {
        await updateCoordinatesWeb();
      }
    } else if (showAlertOnFailure) {
      showAlert();
    }
    loadingCoordinates.value = false;
  }

  /**
   *
   * @returns [long,lat]
   */
  async function getCoordinates(showAlertOnFailure = false): Promise<[number, number] | undefined> {
    if (coordinates.value && isRecent()) {
      return coordinates.value;
    }
    await updateCoordinates(showAlertOnFailure);
    return coordinates.value;
  }

  function isRecent() {
    const thirtySecondsAgo = new Date(Date.now() - 30_000);
    return lastFetchedAt.value && lastFetchedAt.value > thirtySecondsAgo;
  }

  return {
    getCoordinates,
    coordinates,
    loadingCoordinates,
  };
});
